<script setup lang="ts">
import { ref, computed } from 'vue';
import { uploadFile, type UploadResult } from '../composables/useApi';

const emit = defineEmits<{
  close: [];
  complete: [results: UploadResult[]];
}>();

// ── 状态 ──

interface FileItem {
  file: File;
  name: string;
  size: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number; // 0-100
  result?: UploadResult;
  error?: string;
}

const fileItems = ref<FileItem[]>([]);
const uploading = ref(false);
const uploadAllProgress = ref(0);
const dragOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const folderInput = ref<HTMLInputElement | null>(null);

// ── 计算属性 ──

const totalFiles = computed(() => fileItems.value.length);
const successCount = computed(() => fileItems.value.filter(f => f.status === 'success').length);
const errorCount = computed(() => fileItems.value.filter(f => f.status === 'error').length);
const canUpload = computed(() => !uploading.value && fileItems.value.some(f => f.status === 'pending'));
const isAllDone = computed(() => fileItems.value.every(f => f.status === 'success' || f.status === 'error'));

const totalSize = computed(() => {
  const bytes = fileItems.value.reduce((s, f) => s + f.size, 0);
  return formatSize(bytes);
});

// ── 工具函数 ──

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatSizeShort(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

// ── 文件添加 ──

function addFiles(files: FileList | File[]) {
  const items: FileItem[] = [];
  for (const file of Array.from(files)) {
    // Filter video files
    if (!file.type.startsWith('video/') && !file.name.match(/\.(mp4|mkv|webm|mov|avi|mkv)$/i)) {
      continue;
    }
    items.push({
      file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
    });
  }
  fileItems.value = [...fileItems.value, ...items];
}

function onFileSelected(e: Event) {
  const input = e.target as HTMLInputElement;
  if (input.files) {
    addFiles(input.files);
  }
  input.value = '';
}

function onFolderSelected(e: Event) {
  const input = e.target as HTMLInputElement;
  if (input.files) {
    addFiles(input.files);
  }
  input.value = '';
}

// ── 拖放 ──

function onDragOver(e: DragEvent) {
  e.preventDefault();
  dragOver.value = true;
}

function onDragLeave() {
  dragOver.value = false;
}

function onDrop(e: DragEvent) {
  e.preventDefault();
  dragOver.value = false;
  if (e.dataTransfer?.files) {
    addFiles(e.dataTransfer.files);
  }
}

// ── 移除 ──

function removeFile(index: number) {
  fileItems.value.splice(index, 1);
}

function clearAll() {
  if (uploading.value) return;
  fileItems.value = [];
  uploadAllProgress.value = 0;
}

// ── 上传 ──

async function startUpload() {
  if (uploading.value) return;
  uploading.value = true;

  const results: UploadResult[] = [];
  const pending = fileItems.value.filter(f => f.status === 'pending');

  for (let i = 0; i < pending.length; i++) {
    const item = pending[i];
    item.status = 'uploading';
    item.progress = 0;

    try {
      const result = await uploadFile(item.file, (loaded, total) => {
        item.progress = Math.round((loaded / total) * 100);
      });
      item.status = 'success';
      item.progress = 100;
      item.result = result;
      results.push(result);
    } catch (e) {
      item.status = 'error';
      item.error = e instanceof Error ? e.message : 'Upload failed';
    }

    uploadAllProgress.value = Math.round(((i + 1) / pending.length) * 100);
  }

  uploading.value = false;
  emit('complete', results);
}

// ── 关闭 ──

function onClose() {
  if (uploading.value) return;
  emit('close');
}
</script>

<template>
  <div class="upload-overlay" @click.self="onClose">
    <div class="upload-dialog" :class="{ 'is-dragover': dragOver }">
      <!-- Header -->
      <div class="upload-header">
        <h2 class="upload-title">上传视频</h2>
        <button class="close-btn" @click="onClose" :disabled="uploading">✕</button>
      </div>

      <!-- Drop zone (when no files) -->
      <div
        v-if="fileItems.length === 0"
        class="drop-zone"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <div class="drop-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </div>
        <p class="drop-text">拖拽视频文件到此处</p>
        <p class="drop-hint">或点击下方按钮选择</p>
        <div class="drop-actions">
          <button class="btn btn-primary" @click="fileInput?.click()">
            选择文件
          </button>
          <button class="btn btn-secondary" @click="folderInput?.click()">
            选择文件夹
          </button>
        </div>
        <p class="drop-note">支持 MP4, MKV, WebM, MOV, AVI 等 · 单文件最大 95 MB</p>
      </div>

      <!-- File list -->
      <div v-else class="file-list-wrap">
        <div class="file-stats">
          <span>{{ totalFiles }} 个文件</span>
          <span class="stat-sep">·</span>
          <span>{{ totalSize }}</span>
          <span v-if="successCount > 0" class="stat-success">· {{ successCount }} 成功</span>
          <span v-if="errorCount > 0" class="stat-error">· {{ errorCount }} 失败</span>
        </div>

        <!-- Overall progress -->
        <div v-if="uploading" class="overall-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadAllProgress + '%' }"></div>
          </div>
        </div>

        <div class="file-list">
          <div
            v-for="(item, idx) in fileItems"
            :key="idx"
            class="file-row"
            :class="'status-' + item.status"
          >
            <div class="file-info">
              <div class="file-icon">
                <svg v-if="item.status === 'success'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#22c55e" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <svg v-else-if="item.status === 'error'" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#ef4444" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
              </div>
              <div class="file-meta">
                <span class="file-name">{{ item.name }}</span>
                <span class="file-size">{{ formatSize(item.size) }}</span>
              </div>
            </div>

            <div class="file-status">
              <!-- Progress bar -->
              <div v-if="item.status === 'uploading'" class="mini-progress">
                <div class="mini-progress-fill" :style="{ width: item.progress + '%' }"></div>
              </div>
              <span v-else-if="item.status === 'pending'" class="status-badge pending">等待</span>
              <span v-else-if="item.status === 'success'" class="status-badge success">完成</span>
              <span v-else-if="item.status === 'error'" class="status-badge error" :title="item.error">失败</span>
            </div>

            <button
              v-if="item.status === 'pending' && !uploading"
              class="remove-btn"
              @click="removeFile(idx)"
              title="移除"
            >✕</button>
          </div>
        </div>

        <!-- Actions -->
        <div class="file-actions">
          <div class="file-actions-left">
            <button class="btn btn-ghost" @click="fileInput?.click()" :disabled="uploading">
              + 添加文件
            </button>
            <button class="btn btn-ghost" @click="folderInput?.click()" :disabled="uploading">
              + 添加文件夹
            </button>
          </div>
          <div class="file-actions-right">
            <button class="btn btn-ghost" @click="clearAll" :disabled="uploading">
              清空
            </button>
            <button
              v-if="!isAllDone"
              class="btn btn-primary"
              :disabled="!canUpload"
              @click="startUpload"
            >
              {{ uploading ? '上传中...' : '开始上传' }}
            </button>
            <button v-else class="btn btn-primary" @click="onClose">
              完成
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden file inputs -->
    <input
      ref="fileInput"
      type="file"
      multiple
      accept="video/*,.mp4,.mkv,.webm,.mov,.avi"
      style="display:none"
      @change="onFileSelected"
    />
    <input
      ref="folderInput"
      type="file"
      multiple
      webkitdirectory
      style="display:none"
      @change="onFolderSelected"
    />
  </div>
</template>

<style scoped>
.upload-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.15s ease;
}

.upload-dialog {
  background: var(--bg-primary, #1a1a2e);
  border: 1px solid var(--border-color, #2a2a4a);
  border-radius: 16px;
  width: 560px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
  animation: fadeInUp 0.2s ease;
}

.upload-dialog.is-dragover {
  border-color: var(--accent, #6366f1);
  box-shadow: 0 0 0 2px var(--accent-soft, rgba(99, 102, 241, 0.3));
}

/* ── Header ── */

.upload-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border-color, #2a2a4a);
}

.upload-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary, #e8e8f0);
}

.close-btn {
  background: none;
  border: none;
  color: var(--text-muted, #666);
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s;
}
.close-btn:hover:not(:disabled) {
  color: var(--text-primary, #e8e8f0);
  background: var(--bg-secondary, #22223a);
}
.close-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Drop zone ── */

.drop-zone {
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.drop-icon {
  color: var(--text-muted, #666);
  margin-bottom: 8px;
}

.drop-text {
  margin: 0;
  font-size: 16px;
  color: var(--text-primary, #e8e8f0);
  font-weight: 500;
}

.drop-hint {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted, #666);
}

.drop-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.drop-note {
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--text-muted, #555);
}

/* ── Buttons ── */

.btn {
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-primary {
  background: var(--accent, #6366f1);
  color: #fff;
  border-color: var(--accent, #6366f1);
}
.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover, #5558e6);
}

.btn-secondary {
  background: var(--bg-secondary, #22223a);
  color: var(--text-primary, #e8e8f0);
  border-color: var(--border-color, #2a2a4a);
}
.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary, #2a2a4a);
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #999);
  border-color: transparent;
}
.btn-ghost:hover:not(:disabled) {
  background: var(--bg-secondary, #22223a);
  color: var(--text-primary, #e8e8f0);
}

/* ── File stats ── */

.file-list-wrap {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.file-stats {
  padding: 12px 24px;
  font-size: 13px;
  color: var(--text-secondary, #999);
  border-bottom: 1px solid var(--border-color, #2a2a4a);
}

.stat-sep { margin: 0 4px; }
.stat-success { color: #22c55e; }
.stat-error { color: #ef4444; }

.overall-progress {
  padding: 8px 24px;
}

.progress-bar {
  height: 4px;
  background: var(--bg-secondary, #22223a);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--accent, #6366f1);
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* ── File list ── */

.file-list {
  padding: 8px 16px;
  overflow-y: auto;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--bg-secondary, #22223a);
  transition: all 0.15s;
}

.file-row.status-success {
  background: rgba(34, 197, 94, 0.08);
}
.file-row.status-error {
  background: rgba(239, 68, 68, 0.08);
}

.file-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.file-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.file-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.file-name {
  font-size: 13px;
  color: var(--text-primary, #e8e8f0);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 11px;
  color: var(--text-muted, #666);
}

.file-status {
  flex-shrink: 0;
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mini-progress {
  width: 100%;
  height: 4px;
  background: var(--bg-primary, #1a1a2e);
  border-radius: 2px;
  overflow: hidden;
}

.mini-progress-fill {
  height: 100%;
  background: var(--accent, #6366f1);
  border-radius: 2px;
  transition: width 0.2s ease;
}

.status-badge {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
}
.status-badge.pending { background: rgba(255, 255, 255, 0.08); color: var(--text-muted, #666); }
.status-badge.success { background: rgba(34, 197, 94, 0.15); color: #22c55e; }
.status-badge.error { background: rgba(239, 68, 68, 0.15); color: #ef4444; }

.remove-btn {
  background: none;
  border: none;
  color: var(--text-muted, #555);
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}
.remove-btn:hover { color: #ef4444; background: rgba(239, 68, 68, 0.1); }

/* ── File actions ── */

.file-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-top: 1px solid var(--border-color, #2a2a4a);
  gap: 8px;
}

.file-actions-left, .file-actions-right {
  display: flex;
  gap: 4px;
  align-items: center;
}

/* ── Animations ── */

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
